/** Rules strategies (docs/13-rules-strategies.md): per-coin bots that enter and exit on indicator conditions, on any
 *  timeframe from 1 minute to 1 day, with take-profit ladders, stops, trailing, DCA safety orders and time stops.
 *  Everything a user (or the strategy writer) can set lives in this schema; the simulator in packages/core/src/rules
 *  runs it bar by bar, identically in backtests and live. */
import { z } from "zod";
import { Series, TF_MS, TIMEFRAMES, type SeriesRef, type Timeframe } from "./indicators.ts";

// ---------------------------------------------------------------- conditions
/** A value in a comparison: a number, an indicator series, or a series scaled (e.g. 1.02 × EMA 200). */
export const Operand = z.union([
  z.number(), Series,
  z.object({ of: Series, mul: z.number().min(-100).max(100).default(1), add: z.number().default(0) }).strict(),
  // across the coins at each bar: rank (0 = lowest, 1 = highest), z-score, or minus the coins' average
  z.object({ xs: z.enum(["rank", "zscore", "demean"]), of: Series }).strict(),
]);
/** A series as written (defaults such as `out` and `offset` filled in by parsing). */
export type SeriesInput = { ind: string; tf?: Timeframe; offset?: number; out?: string; [param: string]: unknown };
export type Operand = number | SeriesInput | { of: SeriesInput; mul?: number; add?: number } | { xs: "rank" | "zscore" | "demean"; of: SeriesInput };

export type Condition =
  | { all: Condition[] } | { any: Condition[] } | { not: Condition }
  | { cmp: ">" | "<" | ">=" | "<="; a: Operand; b: Operand }
  | { cross: "above" | "below"; a: Operand; b: Operand }
  | { trend: "rising" | "falling"; a: Operand; bars: number }
  | { within: number; when: Condition }
  | { time: { hours?: number[]; days?: number[] } };

export const Condition: z.ZodType<Condition> = z.lazy(() => z.union([
  z.object({ all: z.array(Condition).min(1).max(12) }).strict(),
  z.object({ any: z.array(Condition).min(1).max(12) }).strict(),
  z.object({ not: Condition }).strict(),
  z.object({ cmp: z.enum([">", "<", ">=", "<="]), a: Operand, b: Operand }).strict(),
  z.object({ cross: z.enum(["above", "below"]), a: Operand, b: Operand }).strict(),
  z.object({ trend: z.enum(["rising", "falling"]), a: Operand, bars: z.number().int().min(1).max(50) }).strict(),
  z.object({ within: z.number().int().min(1).max(200), when: Condition }).strict(),          // true if `when` was true on any of the last N bars
  z.object({ time: z.object({ hours: z.array(z.number().int().min(0).max(23)).min(1).optional(), days: z.array(z.number().int().min(0).max(6)).min(1).optional() }).strict() }).strict(), // UTC hours / weekdays (0 = Sunday)
]) as never);

// ---------------------------------------------------------------- strategy
const Pct = (min: number, max: number) => z.number().min(min).max(max);
/** A price distance: a fixed percent from the reference price, or a multiple of ATR at entry. */
export const Distance = z.union([
  z.object({ pct: Pct(0.05, 90) }).strict(),
  z.object({ atr: z.number().min(0.1).max(20), period: z.number().int().min(2).max(200).default(14) }).strict(),
]);
export type Distance = z.infer<typeof Distance>;

export const RulesUniverse = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("manual"), symbols: z.array(z.string().min(2)).min(1).max(30) }),
  z.object({ mode: z.literal("topVolume"), k: z.number().int().min(1).max(30), pool: z.string().default("pool50"), lookbackDays: z.number().int().min(7).max(365).default(30) }),
]);

export const RulesSpec = z.object({
  kind: z.literal("rules"),
  name: z.string().min(1).max(60),
  about: z.string().max(400).optional(),                        // the writer's one-paragraph explanation
  market: z.enum(["spot", "futures"]),
  venue: z.literal("binance").default("binance"),
  quote: z.literal("USDT").default("USDT"),
  universe: RulesUniverse,
  timeframe: z.enum(TIMEFRAMES),
  direction: z.enum(["long", "short", "both"]).default("long"),
  entry: z.object({
    long: Condition.optional(),
    short: Condition.optional(),
    order: z.discriminatedUnion("type", [
      z.object({ type: z.literal("market") }).strict(),
      z.object({ type: z.literal("limit"), offsetPct: Pct(0, 20), expireBars: z.number().int().min(1).max(500).default(3) }).strict(), // below the close for longs, above for shorts
    ]).default({ type: "market" }),
    cooldownBars: z.number().int().min(0).max(10_000).default(0),  // after a coin's trade closes, wait this long before entering it again
  }).strict(),
  sizing: z.object({
    mode: z.enum(["equityPct", "fixed", "risk"]).default("equityPct"),
    value: z.number().positive(),                                  // equityPct: % of account per trade; fixed: USDT per trade; risk: % of account lost if the stop hits
    leverage: z.number().min(1).max(20).default(1),
    maxPositions: z.number().int().min(1).max(30).default(5),
  }).strict(),
  dca: z.object({
    orders: z.number().int().min(1).max(15),                       // safety orders after the first
    stepPct: Pct(0.1, 30),                                         // first safety order this far against the entry
    stepScale: z.number().min(1).max(3).default(1),                // each further step this much wider
    volumeScale: z.number().min(1).max(3).default(1),              // each safety order this much bigger than the previous
    firstSize: z.number().min(0.1).max(10).default(1),             // first safety order relative to the base order
  }).strict().optional(),
  exit: z.object({
    takeProfit: z.object({
      targets: z.array(z.object({ at: Distance, closePct: Pct(1, 100) }).strict()).min(1).max(5), // from the average entry; closePct of the position at entry
      trailing: z.object({ activate: Distance, callbackPct: Pct(0.05, 30) }).strict().optional(), // after `activate`, exit the rest when price gives back callbackPct from its best
    }).strict().optional(),
    stopLoss: z.object({
      at: Distance,                                                // from the average entry (DCA bots: from the last safety order)
      trailing: z.boolean().default(false),                        // follow price at the same distance
      breakevenAt: Distance.optional(),                            // move the stop to entry (plus fees) once price gets this far in profit
    }).strict().optional(),
    when: z.object({ long: Condition.optional(), short: Condition.optional() }).strict().optional(), // signal exits at the next bar's open
    maxBars: z.number().int().min(1).max(100_000).optional(),     // time stop
  }).strict(),
  safety: z.object({
    stopAt: Pct(5, 90).default(30),                                // stop the bot if the account falls this % below its best
    maxDailyLossPct: Pct(1, 50).optional(),                       // no new entries for the rest of the UTC day after losing this much
  }).strict().default({}),
}).strict();
export type RulesSpec = z.infer<typeof RulesSpec>;
export type RulesSpecInput = z.input<typeof RulesSpec>;

export interface RulesIssue { level: "error" | "warning"; field: string; message: string }

/** Every series a spec reads (for warmup and computation). */
export function seriesOf(spec: RulesSpec): SeriesRef[] {
  const out: SeriesRef[] = [];
  const op = (o: Operand) => { if (typeof o === "number") return; out.push(("of" in o ? o.of : o) as SeriesRef); };
  const walk = (c: Condition | undefined) => {
    if (!c) return;
    if ("all" in c) c.all.forEach(walk); else if ("any" in c) c.any.forEach(walk); else if ("not" in c) walk(c.not);
    else if ("within" in c) walk(c.when); else if ("cmp" in c || "cross" in c) { op(c.a); op(c.b); } else if ("trend" in c) op(c.a);
  };
  walk(spec.entry.long); walk(spec.entry.short); walk(spec.exit.when?.long); walk(spec.exit.when?.short);
  return out;
}

const TF_ORDER = TIMEFRAMES as readonly Timeframe[];

export function validateRules(spec: RulesSpec): RulesIssue[] {
  const out: RulesIssue[] = [];
  const e = (field: string, message: string) => out.push({ level: "error", field, message });
  const w = (field: string, message: string) => out.push({ level: "warning", field, message });
  const longs = spec.direction !== "short", shorts = spec.direction !== "long";
  if (spec.market === "spot" && shorts) e("direction", "Spot can't bet down: set direction to long, or trade futures.");
  if (spec.market === "spot" && spec.sizing.leverage !== 1) e("sizing.leverage", "Spot can't borrow: leverage must be 1×.");
  if (longs && !spec.entry.long) e("entry.long", "Add an entry condition for longs.");
  if (shorts && !spec.entry.short) e("entry.short", "Add an entry condition for shorts.");
  const x = spec.exit;
  if (!x.takeProfit && !x.stopLoss && !x.when && !x.maxBars) e("exit", "Add at least one way out: take profit, stop loss, an exit condition or a time limit.");
  if (x.takeProfit) {
    const total = x.takeProfit.targets.reduce((s, t) => s + t.closePct, 0);
    if (total > 100.0001) e("exit.takeProfit", `Take-profit targets close ${total}% of the position; the most is 100%.`);
    if (total < 99.9999 && !x.takeProfit.trailing && !x.stopLoss && !x.when && !x.maxBars) w("exit.takeProfit", `Targets close only ${total}% and nothing closes the rest.`);
  }
  if (spec.sizing.mode === "risk" && !x.stopLoss) e("sizing.mode", "Risk-based sizing needs a stop loss to measure the risk.");
  if (spec.sizing.mode === "equityPct" && spec.sizing.value * spec.sizing.maxPositions > 100 * spec.sizing.leverage) w("sizing", "All positions together can use more than the whole account; later entries will be skipped or smaller.");
  if (spec.dca) {
    const d = spec.dca;
    let vol = 1, dev = 0, step = d.stepPct;
    for (let k = 0; k < d.orders; k++) { vol += d.firstSize * d.volumeScale ** k; dev += step; step *= d.stepScale; }
    if (dev >= 95) e("dca", `The last safety order would sit ${dev.toFixed(0)}% below the entry; reduce steps or their size.`);
    if (spec.market === "futures" && dev * spec.sizing.leverage >= 90) e("dca", `With ${spec.sizing.leverage}× leverage the safety orders reach past the liquidation price.`);
    if (vol > 20) w("dca", `A fully filled deal is ${vol.toFixed(1)}× the first order; size the first order with that in mind.`);
    if (x.stopLoss && x.stopLoss.at && "pct" in x.stopLoss.at && x.stopLoss.at.pct <= d.stepPct) w("exit.stopLoss", "The stop is closer than the first safety order, so safety orders never fill.");
  }
  for (const s of seriesOf(spec)) {
    if (s.tf && TF_ORDER.indexOf(s.tf) < TF_ORDER.indexOf(spec.timeframe)) e("entry", `A condition reads ${s.tf} bars but the bot trades on ${spec.timeframe}; use the bot's timeframe or a longer one.`);
  }
  if (spec.timeframe === "1m" || spec.timeframe === "5m") {
    w("timeframe", spec.market === "spot" ? "Scalping on spot pays 0.1% per trade both ways; most small moves won't cover it. Check fees in the backtest." : "Scalping lives or dies on fees and slippage; check how much of the gross profit fees took in the backtest.");
  }
  if (spec.sizing.leverage > 5) w("sizing.leverage", `${spec.sizing.leverage}× leverage: a ${(100 / spec.sizing.leverage).toFixed(0)}% move against a position wipes its margin.`);
  if (spec.universe.mode === "manual") w("universe", "Hand-picked coins are the ones still alive today, so backtests look better than reality. 'Top by volume' avoids this.");
  return out;
}

/** Fees per side by market (docs/13 §5): spot 0.10% either way; futures 0.02% maker, 0.05% taker. */
export const RULES_FEES = { spot: { maker: 0.001, taker: 0.001 }, futures: { maker: 0.0002, taker: 0.0005 } } as const;

/** Backtests load every coin's bars for the period into memory; this caps a run at a size the backtester handles
 *  in seconds (~6M coin-bars: 1-minute bars for 11 coin-years, 5-minute for 57, hourly for any pool and period). */
export const RULES_MAX_COIN_BARS = 6_000_000;
export function rulesCoinBars(spec: RulesSpec, from: number, to: number, poolSize = 50): number {
  const coins = spec.universe.mode === "manual" ? spec.universe.symbols.length : poolSize;
  return Math.ceil(coins * ((to - from) / TF_MS[spec.timeframe]));
}
/** Readable reason a backtest is too large, or null. */
export function rulesTooBig(spec: RulesSpec, from: number, to: number, poolSize = 50): string | null {
  const n = rulesCoinBars(spec, from, to, poolSize);
  if (n <= RULES_MAX_COIN_BARS) return null;
  const coins = spec.universe.mode === "manual" ? spec.universe.symbols.length : poolSize;
  const maxDays = Math.floor((RULES_MAX_COIN_BARS / coins) * TF_MS[spec.timeframe] / 86_400_000);
  return `That's ${(n / 1e6).toFixed(1)}M bars of ${spec.timeframe} data (${coins} coins). Test at most ${maxDays} days with these coins, use fewer coins, or a longer timeframe.`;
}
